import React, { useState } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import HeartBalloonLogo from './HeartBalloonLogo';
import { MAIN_LINKS } from '../config/siteLinks';
import { detectPlatform } from '../utils/platformDetection';
import { downloadLinkFor, trackStoreClick } from '../utils/appStoreLinks';

const LIGHT_PURPLE = '#A76FFF';

/**
 * Header for the public content pages (Features, FAQ, Blog, legal pages...): logo home, the main
 * sections, and a download button for the visitor's own store. Sticky, so pages need no top offset.
 */
const SiteHeader: React.FC = () => {
    const { platform } = detectPlatform();
    const download = downloadLinkFor(platform, 'header');
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    return (
        <Box component="header" sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)'
        }}>
            <Box sx={{
                maxWidth: 1200,
                mx: 'auto',
                px: { xs: 1.5, sm: 3 },
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, md: 3 }
            }}>
                <Box
                    component="a"
                    href="/"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, textDecoration: 'none', mr: 'auto', minWidth: 0 }}
                >
                    <HeartBalloonLogo size={30} color={LIGHT_PURPLE} />
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.15rem' }, color: LIGHT_PURPLE, whiteSpace: 'nowrap' }}>
                        Pop The Balloon
                    </Typography>
                </Box>

                <Box component="nav" aria-label="Main" sx={{ display: { xs: 'none', md: 'flex' }, gap: 3 }}>
                    {MAIN_LINKS.map(([href, label]) => (
                        <Box
                            key={href}
                            component="a"
                            href={href}
                            sx={{
                                color: '#333',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                textDecoration: 'none',
                                '&:hover': { color: LIGHT_PURPLE }
                            }}
                        >
                            {label}
                        </Box>
                    ))}
                </Box>

                <Box
                    component="a"
                    href={download.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackStoreClick(download.store, 'header', platform)}
                    sx={{
                        bgcolor: '#000',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        px: { xs: 1.5, sm: 2.25 },
                        py: 1,
                        borderRadius: 999,
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': { bgcolor: '#333' }
                    }}
                >
                    {platform === 'ios' ? 'Join iOS beta' : 'Get the app'}
                </Box>

                <IconButton
                    aria-label="Open menu"
                    onClick={event => setMenuAnchor(event.currentTarget)}
                    sx={{ display: { md: 'none' }, color: LIGHT_PURPLE, p: 0.75 }}
                >
                    <MenuIcon />
                </IconButton>
                <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {[['/', 'Home'], ...MAIN_LINKS, ['/about-us', 'About']].map(([href, label]) => (
                        <MenuItem key={href} component="a" href={href} sx={{ minWidth: 180, fontWeight: 500 }}>
                            {label}
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
        </Box>
    );
};

export default SiteHeader;
